import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/contexts/StoreContext";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";

const CartDrawer = () => {
    const {
        cart,
        cartOpen,
        setCartOpen,
        updateQty,
        removeFromCart,
        cartTotal,
        cartCount,
    } = useStore();

    return (
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetContent
                side="right"
                className="bg-background border-l-4 border-ink rounded-none w-full sm:max-w-md p-0 flex flex-col"
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-6 py-5 border-b-4 border-ink space-y-0">
                    <SheetTitle className="font-heading text-3xl tracking-tight uppercase flex items-baseline gap-3">
                        Bag
                        <span className="font-mono-ui text-xs text-muted-foreground tracking-[0.2em]">
                            [{cartCount} ITEMS]
                        </span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        Your shopping bag. Review items, adjust quantity, or continue to the custom checkout.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto" data-testid="cart-items-list">
                    {cart.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                Bag is empty.
                            </p>
                            <Link
                                to="/shop"
                                onClick={() => setCartOpen(false)}
                                className="inline-block mt-6 px-6 py-3 bg-ink text-black font-mono-ui text-xs uppercase tracking-[0.2em] border-2 border-ink hover:bg-hazard hover:border-hazard"
                                data-testid="cart-empty-shop-link"
                            >
                                Browse The Drop
                            </Link>
                        </div>
                    ) : (
                        cart.map((item) => (
                            <div
                                key={item.key}
                                className="flex gap-4 p-4 border-b-2 border-dotted border-ink/40"
                                data-testid={`cart-item-${item.product_id}`}
                            >
                                <div className="w-20 h-24 bg-bone border-2 border-ink overflow-hidden flex-shrink-0">
                                    {item.image && (
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between gap-2">
                                        <h4 className="font-heading text-xl leading-none uppercase tracking-tight">
                                            {item.name}
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => removeFromCart(item.key)}
                                            className="text-muted-foreground hover:text-hazard"
                                            aria-label="Remove"
                                            data-testid={`cart-remove-${item.product_id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        Size {item.size} / {item.color}
                                    </p>
                                    <div className="mt-3 flex justify-between items-center">
                                        <div className="flex items-center border-2 border-ink">
                                            <button
                                                type="button"
                                                onClick={() => updateQty(item.key, item.quantity - 1)}
                                                className="px-2 py-1 hover:bg-ink hover:text-black"
                                                data-testid={`cart-qty-decrease-${item.product_id}`}
                                                aria-label="Decrease quantity"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span className="px-3 font-mono-ui text-sm">
                                                {item.quantity}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => updateQty(item.key, item.quantity + 1)}
                                                className="px-2 py-1 hover:bg-ink hover:text-black"
                                                data-testid={`cart-qty-increase-${item.product_id}`}
                                                aria-label="Increase quantity"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                        <span className="font-mono-ui text-sm">
                                            ${(item.price * item.quantity).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {cart.length > 0 && (
                    <div className="border-t-4 border-foreground p-6 bg-background">
                        <div className="flex justify-between items-baseline mb-1">
                            <span className="font-mono-ui text-xs uppercase tracking-[0.2em]">
                                Subtotal
                            </span>
                            <span className="font-heading text-3xl">
                                ${cartTotal.toFixed(2)}
                            </span>
                        </div>
                        <p className="mb-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            Billing details collected on the checkout form.
                        </p>
                        <Link
                            to="/checkout"
                            onClick={() => setCartOpen(false)}
                            className="block w-full text-center bg-background text-foreground border-2 border-foreground py-4 font-mono-ui text-xs uppercase tracking-[0.2em] hover:bg-hazard hover:border-hazard hover:text-white"
                            data-testid="cart-checkout-button"
                        >
                            Custom Checkout →
                        </Link>
                        <Link
                            to="/cart"
                            onClick={() => setCartOpen(false)}
                            className="block w-full text-center py-3 mt-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-foreground hover:text-hazard"
                            data-testid="cart-view-full-link"
                        >
                            View Full Bag
                        </Link>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CartDrawer;
